// components/canvas/canvas-version-history.js — VERSION HISTORY PANEL
// ═══════════════════════════════════════════════════════════
// Slide-out panel showing version list for an artifact.
// Click a version to preview it, "Restore" to revert.
//
// Usage:
//   <CanvasVersionHistory
//     artifactId={id}
//     supabase={supabase}
//     visible={bool}
//     onClose={fn}
//     onRestore={fn}  — called after successful restore with new content
//   />
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';

function timeAgo(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function CanvasVersionHistory({ artifactId, supabase, visible, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const loadVersions = useCallback(async () => {
    if (!artifactId || !supabase) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/user/artifact-versions?artifactId=${artifactId}`, {
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
      });
      if (res.ok) {
        const { versions: v } = await res.json();
        setVersions(v || []);
      }
    } catch (e) {
      console.error('[version-history] load failed:', e.message);
    } finally {
      setLoading(false);
    }
  }, [artifactId, supabase]);

  useEffect(() => {
    if (visible) loadVersions();
  }, [visible, loadVersions]);

  async function handleRestore(versionId) {
    if (restoring) return;
    setRestoring(versionId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/user/artifact-versions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ action: 'restore', artifactId, versionId }),
      });
      if (res.ok) {
        const { content } = await res.json();
        onRestore?.(content);
        loadVersions(); // Refresh list (new snapshot was auto-created)
      }
    } catch (e) {
      console.error('[version-history] restore failed:', e.message);
    } finally {
      setRestoring(null);
    }
  }

  if (!visible) return null;

  return (
    <div style={{
      position: 'absolute',
      top: 0, right: 0, bottom: 0,
      width: 260,
      background: '#07070a',
      borderLeft: '1px solid #1e1e2a',
      boxShadow: '-4px 0 12px rgba(0,0,0,0.4)',
      zIndex: 20,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        height: 36,
        borderBottom: '1px solid #1e1e2a',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 6,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 10, color: '#9b8fc4', fontFamily: 'Georgia, serif',
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          Version History
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onClose}
          style={{
            background: 'none', border: '1px solid #1e1e2a',
            borderRadius: 4, cursor: 'pointer', color: '#555',
            fontSize: 9, padding: '2px 8px', fontFamily: 'Georgia, serif',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#888'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
        >
          Close
        </button>
      </div>

      {/* Version list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {loading && (
          <div style={{ fontSize: 11, color: '#555', fontFamily: 'Georgia, serif', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>
            Loading...
          </div>
        )}

        {!loading && versions.length === 0 && (
          <div style={{ fontSize: 11, color: '#444', fontFamily: 'Georgia, serif', fontStyle: 'italic', textAlign: 'center', padding: 20, lineHeight: 1.6 }}>
            No versions yet. Versions are auto-saved as you edit.
          </div>
        )}

        {versions.map(v => (
          <div
            key={v.id}
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #141414',
              marginBottom: 6,
              background: '#07070a',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontSize: 9, fontFamily: 'Georgia, serif', fontWeight: 700,
                color: '#9b8fc4', letterSpacing: '0.06em',
              }}>
                v{v.version_number}
              </span>
              <span style={{ fontSize: 9, color: '#444', fontFamily: 'Georgia, serif' }}>
                {timeAgo(v.created_at)}
              </span>
            </div>
            <div style={{
              fontSize: 10, color: '#888', fontFamily: 'Georgia, serif',
              marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {v.title}
            </div>
            <button
              onClick={() => handleRestore(v.id)}
              disabled={restoring === v.id}
              style={{
                marginTop: 6, fontSize: 9, fontFamily: 'Georgia, serif',
                color: restoring === v.id ? '#444' : '#7a9e82',
                background: restoring === v.id ? 'transparent' : 'rgba(122,158,130,0.08)',
                border: '1px solid rgba(122,158,130,0.25)',
                borderRadius: 4, padding: '2px 8px', cursor: restoring === v.id ? 'default' : 'pointer',
              }}
            >
              {restoring === v.id ? 'Restoring...' : 'Restore'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
